import { Currency } from "@redotech/money/currencies";
import {
  CurrencySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RedoShopifyDeliveryMethodType } from "./fulfillment-delivery-method-type";
import { RedoShopifyFulfillmentDisplayStatus } from "./fulfillment-display-status";
import { RedoShopifyFulfillmentHoldReason } from "./fulfillment-hold-reason";
import { RedoShopifyFulfillmentOrderAction } from "./fulfillment-order-action";
import { RedoShopifyFulfillmentOrderRequestStatus } from "./fulfillment-order-request-status";
import { RedoShopifyFulfillmentOrderStatus } from "./fulfillment-order-status";
import { RedoShopifyFulfillmentStatus } from "./fulfillment-status";
import { RedoShopifyOrderDisplayFinancialStatus } from "./order-display-financial-status";
import {
  RedoShopifyOrderDisputeInitiatedAs,
  RedoShopifyOrderDisputeStatus,
} from "./order-disputes";
import { OrderTransactionKind } from "./order-transaction-kind";

const FulfillmentOrderAssignedLocationSchema = z
  .object({
    address1: z
      .string()
      .meta({ privacy: privacy.address_line.merchant })
      .nullish(),
    address2: z
      .string()
      .meta({ privacy: privacy.address_line.merchant })
      .nullish(),
    city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    countryCode: createEnum(RedoShopifyCountryCode, {
      enumName: "RedoShopifyCountryCode",
    }).meta({ privacy: privacy.geo.coarse }),
    location: z
      .object({
        id: z.string().meta({ privacy: privacy.id.external.resource }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    name: z.string().meta({ privacy: privacy.resourceLabel }),
    phone: z.string().meta({ privacy: privacy.phone.merchant }).nullish(),
    province: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  })
  .meta({ privacy: privacy.structured });

const FulfillmentOrderDestinationSchema = z
  .object({
    address1: z
      .string()
      .meta({ privacy: privacy.address_line.customer })
      .nullish(),
    address2: z
      .string()
      .meta({ privacy: privacy.address_line.customer })
      .nullish(),
    city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    company: z
      .string()
      .meta({ privacy: privacy.company_name.customer })
      .nullish(),
    countryCode: createEnum(RedoShopifyCountryCode, {
      enumName: "RedoShopifyCountryCode",
    })
      .nullish()
      .meta({ privacy: privacy.geo.coarse }),
    email: z.string().meta({ privacy: privacy.email.customer }).nullish(),
    firstName: z
      .string()
      .meta({ privacy: privacy.person_name.customer })
      .nullish(),
    lastName: z
      .string()
      .meta({ privacy: privacy.person_name.customer })
      .nullish(),
    location: z
      .object({ id: z.string().meta({ privacy: privacy.nonPii }) })
      .nullish()
      .meta({ privacy: privacy.structured }),
    phone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
    province: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  })
  .meta({ privacy: privacy.structured });

const FulfillmentDeliveryMethodSchema = z
  .object({
    maxDeliveryDateTime: z.coerce.date().nullish(),
    methodType: createEnum(RedoShopifyDeliveryMethodType, {
      enumName: "RedoShopifyDeliveryMethodType",
    }).meta({ privacy: privacy.nonPii }),
    minDeliveryDateTime: z.coerce.date().nullish(),
    presentedName: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    serviceCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    additionalInformation: z
      .object({
        instructions: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        phone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

const FulfillmentHoldsSchema = z
  .object({
    displayReason: z.string().meta({ privacy: privacy.nonPii }),
    id: z.string().meta({ privacy: privacy.nonPii }),
    reason: createEnum(RedoShopifyFulfillmentHoldReason, {
      enumName: "RedoShopifyFulfillmentHoldReason",
    }).meta({ privacy: privacy.nonPii }),
    reasonNotes: z
      .string()
      .meta({ privacy: privacy.text.thirdParty })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });

const FulfillmentOrderSupportedActionSchema_ = z
  .object({
    action: createEnum(RedoShopifyFulfillmentOrderAction, {
      enumName: "RedoShopifyFulfillmentOrderAction",
    }).meta({ privacy: privacy.nonPii }),
    externalUrl: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export const FulfillmentOrderSupportedActionSchema: z.ZodType<FulfillmentOrderSupportedAction> =
  FulfillmentOrderSupportedActionSchema_;
export interface FulfillmentOrderSupportedAction extends z.infer<
  typeof FulfillmentOrderSupportedActionSchema_
> {}

const MoneyBagSchema = z
  .object({
    shopMoney: z
      .object({
        amount: z.string().meta({ privacy: privacy.money }),
        currencyCode: CurrencySchema.meta({ privacy: privacy.money }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
const MoneyBagWithPresentedSchema = MoneyBagSchema.extend({
  presentmentMoney: z.object({
    amount: z.string().meta({ privacy: privacy.money }),
    currencyCode: createEnum(Currency, { enumName: "Currency" }),
  }),
});

const RiskSchema_ = z
  .object({
    assessments: z
      .array(
        z
          .object({
            facts: z
              .array(
                z
                  .object({
                    description: z
                      .string()
                      .meta({ privacy: privacy.text.thirdParty })
                      .nullish(),
                    sentiment: z
                      .string()
                      .meta({ privacy: privacy.nonPii })
                      .nullish(),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
            riskLevel: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RiskSchema: z.ZodType<OrderRisk> = RiskSchema_;

export interface OrderRisk extends z.infer<typeof RiskSchema_> {}
export interface IRisk extends z.ZodType<OrderRisk> {}

const RawFulfillmentOrderLineItemSchema = z
  .object({
    financialSummaries: z
      .array(
        z
          .object({
            approximateDiscountedUnitPriceSet: MoneyBagSchema.meta({
              privacy: privacy.structured,
            }),
            discountAllocations: z
              .array(
                z
                  .object({
                    approximateAllocatedAmountPerItem: MoneyBagSchema.meta({
                      privacy: privacy.structured,
                    }),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
            originalUnitPriceSet: MoneyBagSchema.meta({
              privacy: privacy.structured,
            }),
            quantity: z.number().meta({ privacy: privacy.nonPii }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    id: z.string().meta({ privacy: privacy.nonPii }),
    productTitle: z.string().meta({ privacy: privacy.productCatalogText }),
    variantTitle: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
    image: z
      .object({ url: z.string().meta({ privacy: privacy.publicUrl }) })
      .nullish()
      .meta({ privacy: privacy.structured }),
    inventoryItemId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    lineItem: z
      .object({
        customAttributes: z
          .array(
            z
              .object({
                key: z.string().meta({ privacy: privacy.productCatalogText }),
                value: z
                  .string()
                  .meta({ privacy: privacy.text.customer })
                  .nullish(),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
        id: z.string().meta({ privacy: privacy.nonPii }),
        originalUnitPriceSet: MoneyBagSchema.meta({
          privacy: privacy.structured,
        }),
        quantity: z.number().meta({ privacy: privacy.nonPii }),
        taxLines: z
          .array(
            z
              .object({
                priceSet: MoneyBagSchema.meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
        duties: z
          .array(
            z
              .object({
                harmonizedSystemCode: z
                  .string()
                  .meta({ privacy: privacy.nonPii })
                  .nullish(),
                countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
                  enumName: "RedoShopifyCountryCode",
                })
                  .nullish()
                  .meta({ privacy: privacy.geo.coarse }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    remainingQuantity: z.number().meta({ privacy: privacy.nonPii }),
    requiresShipping: z.boolean().meta({ privacy: privacy.nonPii }),
    totalQuantity: z.number().meta({ privacy: privacy.nonPii }),
    vendor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    variant: z
      .object({
        displayName: z.string().meta({ privacy: privacy.productCatalogText }),
        id: z.string().meta({ privacy: privacy.nonPii }),
        inventoryQuantity: z
          .number()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        bin: z
          .object({
            value: z.string().meta({ privacy: privacy.resourceLabel }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        barcodeMeta: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomDate1: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomDate2: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomDate3: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomString1: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomString2: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomString3: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomNumber1: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomNumber2: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        variantCustomNumber3: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        received: z
          .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        product: z
          .object({
            id: z.string().meta({ privacy: privacy.nonPii }),
            title: z.string().meta({ privacy: privacy.productCatalogText }),
            vendor: z.string().meta({ privacy: privacy.company_name.merchant }),
            tags: z
              .array(z.string().meta({ privacy: privacy.nonPii }))
              .nullish()
              .meta({ privacy: privacy.structured }),
            bin: z
              .object({
                value: z.string().meta({ privacy: privacy.resourceLabel }),
              })
              .nullish()
              .meta({ privacy: privacy.structured }),
            description: z.string().meta({ privacy: privacy.nonPii }),
            productType: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            productCustomDate1: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomDate2: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomDate3: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomString1: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomString2: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomString3: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomNumber1: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomNumber2: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
            productCustomNumber3: z
              .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .meta({ privacy: privacy.structured }),
        inventoryItem: z
          .object({
            harmonizedSystemCode: z
              .string()
              .meta({ privacy: privacy.nonPii })
              .nullish(),
            countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
              enumName: "RedoShopifyCountryCode",
            })
              .nullish()
              .meta({ privacy: privacy.geo.coarse }),
            measurement: z
              .object({
                weight: WeightSchema.nullish().meta({
                  privacy: privacy.structured,
                }),
              })
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .meta({ privacy: privacy.structured }),
        sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
        selectedOptions: z
          .array(
            z
              .object({
                name: z.string().meta({ privacy: privacy.productCatalogText }),
                value: z.string().meta({ privacy: privacy.productCatalogText }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
        barcode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        title: z.string().meta({ privacy: privacy.productCatalogText }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    warnings: z
      .array(
        z
          .object({
            description: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            title: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    weight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export interface RawFulfillmentOrderLineItem extends z.infer<
  typeof RawFulfillmentOrderLineItemSchema
> {}

const RawFulfillmentOrderLocationsForMoveSchema_ = z
  .object({
    nodes: z
      .array(
        z
          .object({
            location: z
              .object({
                id: z.string().meta({ privacy: privacy.id.external.resource }),
                name: z.string().meta({ privacy: privacy.resourceLabel }),
              })
              .meta({ privacy: privacy.structured }),
            movable: z.boolean().meta({ privacy: privacy.flag }),
            message: z
              .string()
              .meta({ privacy: privacy.text.thirdParty })
              .nullish(),
            unavailableLineItemsCount: z
              .object({ count: z.number().meta({ privacy: privacy.nonPii }) })
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RawFulfillmentOrderLocationsForMoveSchema: z.ZodType<RawFulfillmentOrderLocationsForMove> =
  RawFulfillmentOrderLocationsForMoveSchema_;

export interface RawFulfillmentOrderLocationsForMove extends z.infer<
  typeof RawFulfillmentOrderLocationsForMoveSchema_
> {}

const LocalizationExtensionPurpose = ["SHIPPING", "TAX"] as const;

// Should match the requested fields from the fulfillment-fragments.ts
const RawFulfillmentOrderSchema_ = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    assignedLocation: FulfillmentOrderAssignedLocationSchema.meta({
      privacy: privacy.structured,
    }),
    channelId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    deliveryMethod: FulfillmentDeliveryMethodSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    destination: FulfillmentOrderDestinationSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    fulfillAt: z.coerce.date().nullish(),
    fulfillBy: z.coerce.date().nullish(),
    fulfillmentHolds: z
      .array(FulfillmentHoldsSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    fulfillmentLineItems: z
      .array(
        RawFulfillmentOrderLineItemSchema.meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    orderId: z.string().meta({ privacy: privacy.nonPii }),
    orderName: z.string().meta({ privacy: privacy.nonPii }),
    requestStatus: createEnum(RedoShopifyFulfillmentOrderRequestStatus, {
      enumName: "RedoShopifyFulfillmentOrderRequestStatus",
    }).meta({ privacy: privacy.nonPii }),
    status: createEnum(RedoShopifyFulfillmentOrderStatus, {
      enumName: "RedoShopifyFulfillmentOrderStatus",
    }).meta({ privacy: privacy.nonPii }),
    supportedActions: z
      .array(
        FulfillmentOrderSupportedActionSchema_.meta({
          privacy: privacy.structured,
        }),
      )
      .meta({ privacy: privacy.structured }),
    locationsForMove: RawFulfillmentOrderLocationsForMoveSchema_.nullish().meta(
      { privacy: privacy.structured },
    ),
    order: z
      .object({
        customDate1: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customDate2: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customDate3: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customString1: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customString2: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customString3: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customNumber1: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customNumber2: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customNumber3: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        redoShouldSplit: z
          .object({
            value: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        poNumber: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        disputes: z
          .array(
            z
              .object({
                id: z.string().meta({ privacy: privacy.nonPii }),
                initiatedAs: createEnum(RedoShopifyOrderDisputeInitiatedAs, {
                  enumName: "RedoShopifyOrderDisputeInitiatedAs",
                }).meta({ privacy: privacy.nonPii }),
                status: createEnum(RedoShopifyOrderDisputeStatus, {
                  enumName: "RedoShopifyOrderDisputeStatus",
                }).meta({ privacy: privacy.nonPii }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .nullish()
          .meta({ privacy: privacy.structured }),
        billingAddress: z
          .object({
            address1: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            address2: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
            company: z
              .string()
              .meta({ privacy: privacy.company_name.customer })
              .nullish(),
            countryCodeV2: createEnum(RedoShopifyCountryCode, {
              enumName: "RedoShopifyCountryCode",
            })
              .nullish()
              .meta({ privacy: privacy.nonPii }),
            name: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            phone: z
              .string()
              .meta({ privacy: privacy.phone.customer })
              .nullish(),
            province: z
              .string()
              .meta({ privacy: privacy.geo.coarse })
              .nullish(),
            zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        closed: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
        closedAt: z.coerce.date().nullish(),
        fullyPaid: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
        displayFinancialStatus: createEnum(
          RedoShopifyOrderDisplayFinancialStatus,
          { enumName: "RedoShopifyOrderDisplayFinancialStatus" },
        )
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        discountCodes: z
          .array(z.string().meta({ privacy: privacy.id.capability }))
          .nullish()
          .meta({ privacy: privacy.untypedPayload }),
        note: z.string().meta({ privacy: privacy.text.customer }).nullish(),
        customAttributes: z
          .array(
            z
              .object({
                key: z.string().meta({ privacy: privacy.productCatalogText }),
                value: z
                  .string()
                  .meta({ privacy: privacy.text.customer })
                  .nullish(),
              })
              .meta({ privacy: privacy.structured }),
          )
          .nullish()
          .meta({ privacy: privacy.structured }),
        tags: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
        risk: RiskSchema_.nullish().meta({ privacy: privacy.structured }),
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
        shippingLine: z
          .object({
            discountedPriceSet: MoneyBagWithPresentedSchema.meta({
              privacy: privacy.structured,
            }),
            title: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            taxLines: z
              .array(
                z
                  .object({
                    priceSet: MoneyBagSchema.meta({
                      privacy: privacy.structured,
                    }),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        shippingAddress: z
          .object({
            name: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            company: z
              .string()
              .meta({ privacy: privacy.company_name.customer })
              .nullish(),
            address1: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            address2: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
            province: z
              .string()
              .meta({ privacy: privacy.geo.coarse })
              .nullish(),
            zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
            countryCodeV2: createEnum(RedoShopifyCountryCode, {
              enumName: "RedoShopifyCountryCode",
            })
              .nullish()
              .meta({ privacy: privacy.nonPii }),
            phone: z
              .string()
              .meta({ privacy: privacy.phone.customer })
              .nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        transactions: z
          .array(
            z
              .object({
                id: z.string().meta({ privacy: privacy.nonPii }),
                kind: createEnum(OrderTransactionKind, {
                  enumName: "OrderTransactionKind",
                }).meta({ privacy: privacy.nonPii }),
                gateway: z.string().meta({ privacy: privacy.nonPii }).nullish(),
                formattedGateway: z
                  .string()
                  .meta({ privacy: privacy.nonPii })
                  .nullish(),
                paymentDetails: z
                  .object({
                    company: z
                      .string()
                      .meta({ privacy: privacy.nonPii })
                      .nullish(),
                    number: z
                      .string()
                      .meta({ privacy: privacy.nonPii })
                      .nullish(),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                amountSet: MoneyBagWithPresentedSchema.meta({
                  privacy: privacy.structured,
                }),
                parentTransaction: z
                  .object({ id: z.string().meta({ privacy: privacy.nonPii }) })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
        customer: z
          .object({
            id: z.string().meta({ privacy: privacy.nonPii }),
            email: z
              .string()
              .meta({ privacy: privacy.email.customer })
              .nullish(),
            phone: z
              .string()
              .meta({ privacy: privacy.phone.customer })
              .nullish(),
            numberOfOrders: z.coerce.number(),
            displayName: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            note: z.string().meta({ privacy: privacy.text.customer }).nullish(),
            tags: z
              .array(z.string().meta({ privacy: privacy.nonPii }))
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        purchasingEntity: z
          .object({
            __typename: z.string().meta({ privacy: privacy.nonPii }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        channelInformation: z
          .object({
            app: z
              .object({
                developerName: z
                  .string()
                  .meta({ privacy: privacy.nonPii })
                  .nullish(),
              })
              .meta({ privacy: privacy.structured }),
            channelDefinition: z
              .object({
                channelName: z
                  .string()
                  .meta({ privacy: privacy.nonPii })
                  .nullish(),
              })
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        localizationExtensions: z
          .object({
            nodes: z
              .array(
                z
                  .object({
                    countryCode: z
                      .string()
                      .meta({ privacy: privacy.nonPii })
                      .nullish(),
                    key: z.string().meta({ privacy: privacy.nonPii }).nullish(),
                    purpose: createEnum(LocalizationExtensionPurpose, {
                      enumName: "LocalizationExtensionPurpose",
                    }).meta({ privacy: privacy.nonPii }),
                    value: z.string().meta({ privacy: privacy.id.government }),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RawFulfillmentOrderSchema: z.ZodType<RawFulfillmentOrder> =
  RawFulfillmentOrderSchema_;

export interface RawFulfillmentOrder extends z.infer<
  typeof RawFulfillmentOrderSchema_
> {}

const RawFulfillmentLineItemSchema = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    quantity: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    lineItem: z
      .object({
        image: z
          .object({ url: z.string().meta({ privacy: privacy.publicUrl }) })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customAttributes: z
          .array(
            z
              .object({
                key: z.string().meta({ privacy: privacy.nonPii }),
                value: z
                  .string()
                  .meta({ privacy: privacy.text.customer })
                  .nullish(),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
        id: z.string().meta({ privacy: privacy.nonPii }),
        discountedUnitPriceSet: MoneyBagSchema.meta({
          privacy: privacy.structured,
        }),
        originalUnitPriceSet: MoneyBagSchema.meta({
          privacy: privacy.structured,
        }),
        quantity: z.number().meta({ privacy: privacy.nonPii }),
        currentQuantity: z.number().meta({ privacy: privacy.nonPii }),
        variantTitle: z
          .string()
          .meta({ privacy: privacy.productCatalogText })
          .nullish(),
        requiresShipping: z.boolean().meta({ privacy: privacy.nonPii }),
        variant: z
          .object({
            displayName: z
              .string()
              .meta({ privacy: privacy.productCatalogText }),
            inventoryQuantity: z
              .number()
              .meta({ privacy: privacy.nonPii })
              .nullish(),
            id: z.string().meta({ privacy: privacy.nonPii }),
            bin: z
              .object({
                value: z.string().meta({ privacy: privacy.resourceLabel }),
              })
              .nullish()
              .meta({ privacy: privacy.structured }),
            barcode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            product: z
              .object({
                id: z.string().meta({ privacy: privacy.nonPii }),
                title: z.string().meta({ privacy: privacy.productCatalogText }),
                vendor: z
                  .string()
                  .meta({ privacy: privacy.company_name.merchant }),
                bin: z
                  .object({
                    value: z.string().meta({ privacy: privacy.resourceLabel }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
            selectedOptions: z
              .array(
                z
                  .object({
                    name: z
                      .string()
                      .meta({ privacy: privacy.productCatalogText }),
                    value: z
                      .string()
                      .meta({ privacy: privacy.productCatalogText }),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
            title: z.string().meta({ privacy: privacy.productCatalogText }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
        taxLines: z
          .array(
            z
              .object({
                priceSet: MoneyBagSchema.meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export interface RawFulfillmentLineItem extends z.infer<
  typeof RawFulfillmentLineItemSchema
> {}

const FulfillmentOriginAddressSchema = z
  .object({
    address1: z
      .string()
      .meta({ privacy: privacy.address_line.merchant })
      .nullish(),
    address2: z
      .string()
      .meta({ privacy: privacy.address_line.merchant })
      .nullish(),
    city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    countryCode: createEnum(RedoShopifyCountryCode, {
      enumName: "RedoShopifyCountryCode",
    }).meta({ privacy: privacy.nonPii }),
    provinceCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  })
  .meta({ privacy: privacy.structured });

// Should match the requested fields from the fulfillment-fragments.ts
const RawFulfillmentSchema_ = z
  .object({
    id: z.string().meta({ privacy: privacy.id.external.resource }),
    name: z.string().meta({ privacy: privacy.id.external.resource }),
    originAddress: FulfillmentOriginAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    location: z
      .object({ id: z.string().meta({ privacy: privacy.nonPii }) })
      .nullish()
      .meta({ privacy: privacy.structured }),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    estimatedDeliveryAt: z.coerce.date().nullish(),
    inTransitAt: z.coerce.date().nullish(),
    deliveredAt: z.coerce.date().nullish(),
    status: createEnum(RedoShopifyFulfillmentStatus, {
      enumName: "RedoShopifyFulfillmentStatus",
    }).meta({ privacy: privacy.nonPii }),
    displayStatus: createEnum(RedoShopifyFulfillmentDisplayStatus, {
      enumName: "RedoShopifyFulfillmentDisplayStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    requiresShipping: z.boolean().meta({ privacy: privacy.nonPii }),
    totalQuantity: z.number().meta({ privacy: privacy.nonPii }),
    fulfillmentLineItems: z
      .array(RawFulfillmentLineItemSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    trackingInfo: z
      .array(
        z
          .object({
            company: z
              .string()
              .meta({ privacy: privacy.company_name.thirdParty })
              .nullish(),
            url: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
            number: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    order: z
      .object({
        id: z.string().meta({ privacy: privacy.id.external.resource }),
        name: z.string().meta({ privacy: privacy.id.external.resource }),
        note: z.string().meta({ privacy: privacy.text.customer }).nullish(),
        tags: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
        createdAt: z.coerce.date(),
        shippingAddress: z
          .object({
            address1: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            address2: z
              .string()
              .meta({ privacy: privacy.address_line.customer })
              .nullish(),
            city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
            company: z
              .string()
              .meta({ privacy: privacy.company_name.customer })
              .nullish(),
            countryCodeV2: createEnum(RedoShopifyCountryCode, {
              enumName: "RedoShopifyCountryCode",
            })
              .nullish()
              .meta({ privacy: privacy.nonPii }),
            email: z
              .string()
              .meta({ privacy: privacy.email.customer })
              .nullish(),
            firstName: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            lastName: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            phone: z
              .string()
              .meta({ privacy: privacy.phone.customer })
              .nullish(),
            province: z
              .string()
              .meta({ privacy: privacy.geo.coarse })
              .nullish(),
            zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        customer: z
          .object({
            id: z.string().meta({ privacy: privacy.nonPii }),
            email: z
              .string()
              .meta({ privacy: privacy.email.customer })
              .nullish(),
            phone: z
              .string()
              .meta({ privacy: privacy.phone.customer })
              .nullish(),
            numberOfOrders: z.coerce.number(),
            displayName: z
              .string()
              .meta({ privacy: privacy.person_name.customer })
              .nullish(),
            note: z.string().meta({ privacy: privacy.text.customer }).nullish(),
            tags: z
              .array(z.string().meta({ privacy: privacy.nonPii }))
              .nullish()
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
        localizationExtensions: z
          .object({
            nodes: z
              .array(
                z
                  .object({
                    purpose: createEnum(LocalizationExtensionPurpose, {
                      enumName: "LocalizationExtensionPurpose",
                    }).meta({ privacy: privacy.nonPii }),
                    value: z.string().meta({ privacy: privacy.id.government }),
                  })
                  .meta({ privacy: privacy.structured }),
              )
              .meta({ privacy: privacy.structured }),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RawFulfillmentSchema: z.ZodType<RawFulfillment> =
  RawFulfillmentSchema_;
export interface RawFulfillment extends z.infer<typeof RawFulfillmentSchema_> {}

const RawFulfillmentOrderSyncDataSchema_ = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    updatedAt: z.coerce.date(),
    deliveryMethod: FulfillmentDeliveryMethodSchema.pick({ methodType: true })
      .nullish()
      .meta({ privacy: privacy.structured }),
    order: z
      .object({
        createdAt: z.coerce.date(),
        updatedAt: z.coerce.date(),
        tags: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RawFulfillmentOrderSyncDataSchema: z.ZodType<RawFulfillmentOrderSyncData> =
  RawFulfillmentOrderSyncDataSchema_;

export interface RawFulfillmentOrderSyncData extends z.infer<
  typeof RawFulfillmentOrderSyncDataSchema_
> {}

const RawProductUpdateSyncDataSchema_ = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    updatedAt: z.coerce.date(),
    bin: z
      .object({ value: z.string().meta({ privacy: privacy.nonPii }) })
      .nullish()
      .meta({ privacy: privacy.structured }),
    tags: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    title: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    variants: z
      .object({
        nodes: z
          .array(
            z
              .object({
                id: z.string().meta({ privacy: privacy.nonPii }),
                bin: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                barcode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
                sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
                inventoryItem: z
                  .object({
                    measurement: z
                      .object({
                        weight: WeightSchema.nullish().meta({
                          privacy: privacy.structured,
                        }),
                      })
                      .nullish()
                      .meta({ privacy: privacy.structured }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomDate1: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomDate2: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomDate3: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomString1: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomString2: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomString3: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomNumber1: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomNumber2: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                variantCustomNumber3: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
                received: z
                  .object({
                    value: z.string().meta({ privacy: privacy.nonPii }),
                  })
                  .nullish()
                  .meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const RawProductUpdateSyncDataSchema: z.ZodType<RawProductUpdateSyncData> =
  RawProductUpdateSyncDataSchema_;
export interface RawProductUpdateSyncData extends z.infer<
  typeof RawProductUpdateSyncDataSchema_
> {}

export enum ShopifyRiskAssessmentResult {
  /** Indicates a high likelihood that the order is fraudulent. */
  High = "HIGH",
  /** Indicates a low likelihood that the order is fraudulent. */
  Low = "LOW",
  /** Indicates a medium likelihood that the order is fraudulent. */
  Medium = "MEDIUM",
  /** Indicates that the risk assessment will not provide a recommendation for the order. */
  None = "NONE",
  /** Indicates that the risk assessment is still pending. */
  Pending = "PENDING",
}

export const riskLabels: Record<ShopifyRiskAssessmentResult, string> = {
  [ShopifyRiskAssessmentResult.High]: "High",
  [ShopifyRiskAssessmentResult.Low]: "Low",
  [ShopifyRiskAssessmentResult.Medium]: "Medium",
  [ShopifyRiskAssessmentResult.None]: "None",
  [ShopifyRiskAssessmentResult.Pending]: "Pending",
};

export const ShopifyFulfillmentHoldDisplayNames: Record<
  RedoShopifyFulfillmentHoldReason,
  string
> = {
  [RedoShopifyFulfillmentHoldReason.AwaitingPayment]: "Awaiting Payment",
  [RedoShopifyFulfillmentHoldReason.AwaitingReturnItems]:
    "Awaiting Return Items",
  [RedoShopifyFulfillmentHoldReason.HighRiskOfFraud]: "High Risk of Fraud",
  [RedoShopifyFulfillmentHoldReason.IncorrectAddress]: "Incorrect Address",
  [RedoShopifyFulfillmentHoldReason.InventoryOutOfStock]:
    "Inventory Out of Stock",
  [RedoShopifyFulfillmentHoldReason.OnlineStorePostPurchaseCrossSell]:
    "Online Store Post Purchase Cross Sell",
  [RedoShopifyFulfillmentHoldReason.Other]: "Other",
  [RedoShopifyFulfillmentHoldReason.UnknownDeliveryDate]:
    "Unknown Delivery Date",
};
